import { Group, Stack, Table, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  LoadingScreen,
  PageHeader,
} from "@xplatform/design-system";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { releaseNotesApi } from "@services/releaseNotesApi";
import type { ReleaseNote } from "@models/releaseNotes";
import { useReleaseNotesPermissions } from "@hooks/usePermissions";
import { useLocalized } from "@utils/localizedText";
import { ReleaseNoteForm, type FormValue } from "./ReleaseNoteForm";

/**
 * The authoring surface, as a permissioned route inside this module rather than
 * a component the admin module mounts.
 *
 * A third federation expose would have meant admin-frontend consuming another
 * remote, and no remote consumes a remote today: only the shell does. Admin can
 * link here instead, which costs an anchor tag and invents no new dependency
 * direction.
 */
export const ManageReleasesPage = () => {
  const { t } = useTranslation();
  const { pick, formatDate } = useLocalized();
  const { resolved, canManageReleaseNotes } = useReleaseNotesPermissions();

  const [notes, setNotes] = useState<ReleaseNote[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [editing, setEditing] = useState<ReleaseNote | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const load = useCallback(() => {
    if (!canManageReleaseNotes) return;
    releaseNotesApi.admin
      .list()
      .then((page) => setNotes(page.items))
      .catch((cause: Error) => setError(cause));
  }, [canManageReleaseNotes]);

  useEffect(load, [load]);

  if (!resolved) return <LoadingScreen message={t("common.loading")} />;

  if (!canManageReleaseNotes) {
    return (
      <Stack p="lg">
        <EmptyState
          title={t("manage.denied.title")}
          description={t("manage.denied.body")}
        />
      </Stack>
    );
  }

  const submit = async (value: FormValue) => {
    if (editing) {
      await releaseNotesApi.admin.update(editing.module, editing.version, {
        title: value.title,
        highlight: value.highlight,
        changes: value.changes,
        released_at: value.released_at,
      });
    } else {
      await releaseNotesApi.admin.create(value);
    }
    load();
  };

  const publish = async (note: ReleaseNote) => {
    setProblems([]);
    try {
      await releaseNotesApi.admin.publish(note.module, note.version);
      load();
    } catch {
      // The API answers 422 with every missing field at once, so the author
      // fixes them in one pass instead of one per attempt.
      setProblems([t("manage.publishFailed", { version: note.version })]);
    }
  };

  const unpublish = async (note: ReleaseNote) => {
    await releaseNotesApi.admin.unpublish(note.module, note.version);
    load();
  };

  const remove = async (note: ReleaseNote) => {
    await releaseNotesApi.admin.remove(note.module, note.version);
    load();
  };

  return (
    <Stack gap="lg" p="lg">
      <PageHeader
        title={t("manage.title")}
        description={t("manage.description")}
      />

      <Group justify="flex-end">
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          {t("manage.create")}
        </Button>
      </Group>

      {problems.length > 0 && (
        <Alert color="orange" title={t("manage.publishBlocked")}>
          {problems.map((problem) => (
            <Text key={problem} size="sm">
              {problem}
            </Text>
          ))}
        </Alert>
      )}

      {error && (
        <Alert color="red" title={t("releases.page.errorTitle")}>
          {t("releases.page.errorBody")}
        </Alert>
      )}

      {!notes && !error && <LoadingScreen message={t("common.loading")} />}

      {notes && notes.length === 0 && (
        <EmptyState
          title={t("manage.emptyTitle")}
          description={t("manage.emptyBody")}
        />
      )}

      {notes && notes.length > 0 && (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("manage.table.module")}</Table.Th>
              <Table.Th>{t("manage.table.version")}</Table.Th>
              <Table.Th>{t("manage.table.releasedAt")}</Table.Th>
              <Table.Th>{t("manage.table.title")}</Table.Th>
              <Table.Th>{t("manage.table.status")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {notes.map((note) => (
              <Table.Tr key={`${note.module}-${note.version}`}>
                <Table.Td>{t(`releases.module.${note.module}`)}</Table.Td>
                <Table.Td>{note.version}</Table.Td>
                <Table.Td>{formatDate(note.released_at)}</Table.Td>
                <Table.Td>{pick(note.title)}</Table.Td>
                <Table.Td>
                  <Badge color={note.status === "published" ? "green" : "gray"}>
                    {t(`manage.status.${note.status}`)}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => {
                        setEditing(note);
                        setFormOpen(true);
                      }}
                    >
                      {t("common.edit")}
                    </Button>
                    {note.status === "draft" ? (
                      <Button size="xs" onClick={() => publish(note)}>
                        {t("manage.publish")}
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => unpublish(note)}
                      >
                        {t("manage.unpublish")}
                      </Button>
                    )}
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => remove(note)}
                    >
                      {t("common.delete")}
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {formOpen && (
        <ReleaseNoteForm
          // Remount per target so the form state resets between edits.
          key={editing ? `${editing.module}-${editing.version}` : "new"}
          opened={formOpen}
          existing={editing}
          onClose={() => setFormOpen(false)}
          onSubmit={submit}
        />
      )}
    </Stack>
  );
};
